import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader } from './ui/Card';
import { CardListToolbar } from './ui/CardListToolbar';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { AlertMessage } from './ui/AlertMessage';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { EmptyState } from './ui/EmptyState';
import { SkeletonRow } from './ui/Skeleton';
import { BrokerFormModal } from './BrokerFormModal';
import { usePermissions } from '../permissions/PermissionContext';
import { api, extractErrorMessage } from '../api/client';

function Brokers() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editingItem, setEditingItem] = useState(null);
  const [pageMessage, setPageMessage] = useState('');
  const [pageError, setPageError] = useState('');
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { ownerAdmin } = usePermissions();
  const canMutate = ownerAdmin;

  const fetchBrokers = async () => {
    setLoading(true);
    setPageError('');
    try {
      const { data } = await api.get('/api/brokers');
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems([]);
      setPageError(extractErrorMessage(err, 'Unable to load brokers'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrokers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pageMessage) return undefined;
    const timer = setTimeout(() => setPageMessage(''), 3500);
    return () => clearTimeout(timer);
  }, [pageMessage]);

  useEffect(() => {
    if (!pageError) return undefined;
    const timer = setTimeout(() => setPageError(''), 5000);
    return () => clearTimeout(timer);
  }, [pageError]);

  const filtered = items.filter((b) => {
    const key = search.trim().toLowerCase();
    if (!key) return true;
    return (
      String(b.name || '').toLowerCase().includes(key) ||
      String(b.contact || '').toLowerCase().includes(key)
    );
  });

  const openCreate = () => {
    setFormMode('create');
    setEditingItem(null);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setFormMode('edit');
    setEditingItem(row);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormMode('create');
    setEditingItem(null);
  };

  const handleSaved = (saved) => {
    const isCreate = formMode === 'create';
    setItems((prev) => (isCreate ? [saved, ...prev] : prev.map((x) => (x.id === saved.id ? saved : x))));
    setPageMessage(isCreate ? 'Broker created successfully' : 'Broker updated successfully');
    closeForm();
  };

  const handleDelete = async () => {
    if (!confirmDeleteItem) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/api/brokers/${confirmDeleteItem.id}`);
      setItems((prev) => prev.filter((x) => x.id !== confirmDeleteItem.id));
      setPageMessage('Broker deleted successfully');
      setConfirmDeleteItem(null);
    } catch (err) {
      setConfirmDeleteItem(null);
      setPageError(extractErrorMessage(err, 'Unable to delete broker'));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div>
      <Card>
        <CardHeader variant="gradient">
          <CardListToolbar
            tone="light"
            title="Brokers"
            actions={(
              <>
                <div className="w-full sm:w-64">
                  <Input
                    type="text"
                    placeholder="Search brokers..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    leftIcon={(
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    )}
                  />
                </div>
                {canMutate ? (
                <Button
                  type="button"
                  variant="primary"
                  onClick={openCreate}
                  leftIcon={(
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  )}
                >
                  Add Broker
                </Button>
                ) : null}
              </>
            )}
          />
        </CardHeader>
        <CardBody>
          <AlertMessage message={pageMessage} type="success" onClose={() => setPageMessage('')} className="mb-4" />
          <AlertMessage message={pageError} type="error" onClose={() => setPageError('')} className="mb-4" />

          {!loading && filtered.length === 0 ? (
            <EmptyState
              title={search ? 'No brokers match your search' : 'No brokers yet'}
              description={search ? 'Try a different search term.' : 'Add brokers you work with.'}
              action={!search && canMutate ? <Button type="button" variant="primary" onClick={openCreate}>Add Broker</Button> : null}
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th className="w-14">#</th>
                    <th>Name</th>
                    <th>Contact</th>
                    <th className="text-right pr-6">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} columns={4} />)
                    : filtered.map((row, i) => (
                      <tr key={row.id}>
                        <td className="text-slate-400 tabular-nums">{i + 1}</td>
                        <td className="font-medium text-slate-800">{row.name || '—'}</td>
                        <td className="text-slate-700 text-sm max-w-[200px]">
                          <span className="block truncate" title={row.contact || ''}>{row.contact || '—'}</span>
                        </td>
                        <td className="pr-6">
                          <div className="flex items-center justify-end gap-1.5">
                            {canMutate && (
                            <button
                              type="button"
                              onClick={() => openEdit(row)}
                              className="icon-action icon-action-primary"
                              title="Edit"
                              aria-label={`Edit ${row.name}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            )}
                            {canMutate && (
                            <button
                              type="button"
                              onClick={() => {
                                setPageError('');
                                setPageMessage('');
                                setConfirmDeleteItem(row);
                              }}
                              className="icon-action icon-action-danger"
                              title="Delete"
                              aria-label={`Delete ${row.name}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <BrokerFormModal
        open={showForm}
        onClose={closeForm}
        mode={formMode}
        record={editingItem}
        onSaved={handleSaved}
        formId="broker-master-form"
      />

      <ConfirmDialog
        open={Boolean(confirmDeleteItem)}
        title="Delete Broker"
        message={confirmDeleteItem ? `Delete broker "${confirmDeleteItem.name}"?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteItem(null)}
        loading={deleteLoading}
        variant="danger"
      />
    </div>
  );
}

export default Brokers;
